import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, DollarSign, Calendar } from 'lucide-react'
import { usePortfolioStore } from '../store/portfolioStore'
import { useDividendStore, DividendRecord, DividendInput } from '../store/dividendStore'
import { useThemeStore } from '../store/themeStore'
import Button from './ui/Button'
import Select from './ui/Select'

interface AddDividendModalProps {
  isOpen: boolean
  onClose: () => void
  editing?: DividendRecord | null
}

const emptyForm = {
  symbol: '',
  name: '',
  port_api_type: 'th_stock' as DividendInput['port_api_type'],
  amount_per_share: '',
  currency: 'THB' as 'USD' | 'THB',
  ex_date: '',
  pay_date: '',
  shares_held: '',
  notes: '',
}

const AddDividendModal: React.FC<AddDividendModalProps> = ({ isOpen, onClose, editing }) => {
  const { portfolio } = usePortfolioStore()
  const { addDividend, updateDividend } = useDividendStore()
  const { isDark } = useThemeStore()

  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(false)

  // Unique holdings for dropdown
  const holdingOptions = portfolio.map(h => ({
    value: h.symbol,
    label: `${h.symbol} — ${h.name}`,
  }))

  useEffect(() => {
    if (!isOpen) return
    if (editing) {
      setForm({
        symbol: editing.symbol,
        name: editing.name,
        port_api_type: editing.port_api_type,
        amount_per_share: String(editing.amount_per_share),
        currency: editing.currency,
        ex_date: editing.ex_date,
        pay_date: editing.pay_date || '',
        shares_held: String(editing.shares_held),
        notes: editing.notes || '',
      })
    } else {
      setForm(emptyForm)
    }
  }, [isOpen, editing])

  // When symbol changes, auto-fill name, port_api_type, currency, shares_held
  const handleSymbolChange = (symbol: string) => {
    const holding = portfolio.find(h => h.symbol === symbol)
    if (holding) {
      setForm(f => ({
        ...f,
        symbol,
        name: holding.name,
        port_api_type: holding.port_api_type,
        currency: holding.purchase_currency,
        shares_held: String(holding.shares),
      }))
    } else {
      setForm(f => ({ ...f, symbol }))
    }
  }

  const totalReceived =
    parseFloat(form.amount_per_share || '0') * parseFloat(form.shares_held || '0')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.symbol || !form.amount_per_share || !form.ex_date || !form.shares_held) return
    setLoading(true)
    try {
      const payload: DividendInput = {
        symbol: form.symbol,
        name: form.name,
        port_api_type: form.port_api_type,
        amount_per_share: parseFloat(form.amount_per_share),
        currency: form.currency,
        ex_date: form.ex_date,
        pay_date: form.pay_date || null,
        shares_held: parseFloat(form.shares_held),
        total_received: totalReceived,
        notes: form.notes || null,
      }
      if (editing) {
        await updateDividend(editing.id, payload)
      } else {
        await addDividend(payload)
      }
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
    isDark
      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`
  const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`

  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="sheet fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        onClick={e => { if (e.target === e.currentTarget) onClose() }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <DollarSign className="w-4 h-4 text-gain" />
              </div>
              <h2 className="text-base font-semibold">
                {editing ? 'แก้ไขเงินปันผล' : 'บันทึกเงินปันผล'}
              </h2>
            </div>
            <button onClick={onClose} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Symbol */}
            <div>
              <label className={labelClass}>หุ้น / กองทุน *</label>
              <Select
                value={form.symbol}
                onChange={v => handleSymbolChange(v)}
                options={holdingOptions}
                placeholder="เลือกจากพอร์ต..."
              />
            </div>

            {/* Amount per share + Currency */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>ปันผล/หุ้น (บาท) *</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.amount_per_share}
                  onChange={e => setForm(f => ({ ...f, amount_per_share: e.target.value }))}
                  placeholder="0.00"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>สกุลเงิน</label>
                <Select
                  value={form.currency}
                  onChange={v => setForm(f => ({ ...f, currency: v as 'USD' | 'THB' }))}
                  options={[{ value: 'THB', label: '🇹🇭 THB' }, { value: 'USD', label: '🇺🇸 USD' }]}
                />
              </div>
            </div>

            {/* Shares held */}
            <div>
              <label className={labelClass}>จำนวนหุ้นที่ถือ (ณ วัน XD) *</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={form.shares_held}
                onChange={e => setForm(f => ({ ...f, shares_held: e.target.value }))}
                placeholder="0"
                className={inputClass}
                required
              />
            </div>

            {/* Total received (readonly) */}
            {totalReceived > 0 && (
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium ${
                isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700'
              }`}>
                <span>เงินปันผลรวม</span>
                <span>{totalReceived.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} {form.currency}</span>
              </div>
            )}

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>
                  <Calendar className="w-3 h-3 inline mr-1" />
                  วัน XD *
                </label>
                <input
                  type="date"
                  value={form.ex_date}
                  onChange={e => setForm(f => ({ ...f, ex_date: e.target.value }))}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  <Calendar className="w-3 h-3 inline mr-1" />
                  วันจ่าย
                </label>
                <input
                  type="date"
                  value={form.pay_date}
                  onChange={e => setForm(f => ({ ...f, pay_date: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className={labelClass}>หมายเหตุ</label>
              <input
                type="text"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="เช่น งวดที่ 1/2026"
                className={inputClass}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button variant="secondary" className="flex-1" type="button" onClick={onClose}>
                ยกเลิก
              </Button>
              <Button className="flex-1"
                type="submit"
                loading={loading}
                disabled={!form.symbol || !form.amount_per_share || !form.ex_date || !form.shares_held}
              >
                {editing ? 'บันทึก' : 'เพิ่ม'}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

export default AddDividendModal
